// Géométrie du chemin Reps : où se pose chaque nœud d'une unité, et où
// tombent les creux qui accueillent les décors.
//
// POURQUOI CE MODULE. Le zigzag était un motif de longueur fixe
// ([0, 44, 68, 44, 0, -44, -68, -44]) échantillonné modulo le rang de la
// leçon. Une unité plus courte que le motif s'arrêtait en plein virage (une
// unité de 7 finissait son boss collé au bord) ; une unité plus longue
// redessinait le début à l'identique (effet de boucle, pas de chemin) ; et le
// sens était choisi sur la parité du numéro d'unité, donc deux unités
// voisines pouvaient pencher du même côté de part et d'autre de la bannière.
//
// LA RÈGLE. On ne suit plus un motif, on découpe l'unité en VIRAGES entiers :
// chaque virage est une demi-onde complète qui part du centre et y revient,
// et les virages alternent de côté. Trois conséquences directes :
//   - le premier et le dernier nœud d'une unité sont toujours au centre, donc
//     le chemin entre sous la bannière et en ressort proprement ;
//   - aucun virage n'est jamais coupé, quel que soit le nombre de leçons ;
//   - les creux sont une DONNÉE de la forme, plus quelque chose qu'il faut
//     redeviner après coup à partir des offsets (cf. decorationsForUnit).
//
// PARITÉ MOBILE OBLIGATOIRE : copie stricte dans
// reps-app/src/features/path/path-shape.ts. Le harnais
// docs/reps/path-parity/run.mjs échoue si les deux divergent.

/** Écart horizontal maximal (px) du chemin par rapport au centre. */
export const BEND_AMPLITUDE = 68;

/** Pas horizontal maximal (px) entre deux nœuds voisins. C'est lui qui donne
 * son CARACTÈRE au chemin : au-delà, le zigzag devient un accordéon. Un
 * virage court doit donc creuser moins profond pour tenir la même pente. */
export const MAX_STEP = 48;

/** Longueur maximale d'un virage, en pas (intervalles entre nœuds). Au-delà,
 * la demi-onde s'étale et le chemin devient un arc mou. C'est ce plafond qui
 * décide combien de virages porte une unité. */
export const BEND_MAX_SPAN = 5;

/** Distance verticale (px) entre deux nœuds voisins : hauteur de la pastille
 * (face écrasée 55 + lèvre 3D 8) + la marge basse du nœud (28). Sert à
 * descendre un décor d'un demi-pas quand son creux tombe entre deux nœuds.
 * À garder aligné sur PathNode (taille des nœuds et mb-7). */
export const NODE_PITCH = 91;

/**
 * Découpage d'une unité en virages : renvoie la longueur (en pas) de chacun.
 * Les longueurs ne diffèrent jamais de plus d'un pas entre elles, et les plus
 * longues viennent en tête — le virage le plus ample tombe juste sous la
 * bannière d'unité. Une unité de 0 ou 1 nœud n'a pas de chemin à dessiner.
 */
export function unitBends(nodeCount) {
    const spans = nodeCount - 1;
    if (spans < 1) return [];
    const count = Math.max(1, Math.ceil(spans / BEND_MAX_SPAN));
    const base = Math.floor(spans / count);
    const extra = spans % count;
    const bends = [];
    for (let i = 0; i < count; i++) bends.push(base + (i < extra ? 1 : 0));
    return bends;
}

/** Profondeur d'un virage : plafonnée par l'amplitude du chemin, mais surtout
 * par le pas horizontal — un virage de 3 pas creuse moins qu'un virage de 4
 * pour que la pente reste la même. Un virage d'un seul pas ne creuse rien. */
function bendAmplitude(span) {
    if (span < 2) return 0;
    return Math.min(BEND_AMPLITUDE, MAX_STEP / Math.sin(Math.PI / span));
}

/**
 * Décalage horizontal (px depuis le centre) de chaque nœud de l'unité.
 * `startSign` vaut +1 si le premier virage part vers la droite, -1 vers la
 * gauche. Le tableau fait exactement `nodeCount` valeurs, la première et la
 * dernière à 0.
 */
export function unitOffsets(nodeCount, startSign) {
    if (nodeCount <= 0) return [];
    const offsets = [0];
    let sign = startSign;
    for (const span of unitBends(nodeCount)) {
        const amplitude = bendAmplitude(span);
        for (let step = 1; step <= span; step++) {
            // `|| 0` : sin(π) ne vaut pas exactement zéro en flottant, et un
            // -0 se propagerait jusque dans les transformations CSS.
            offsets.push(
                Math.round(
                    sign * amplitude * Math.sin((Math.PI * step) / span)
                ) || 0
            );
        }
        sign = -sign;
    }
    return offsets;
}

/**
 * Emplacements de décor de l'unité, un par virage, dans l'ordre du chemin :
 * le décor se pose au sommet du virage, dans la moitié VIDE de la colonne.
 * L'admin en remplit jusqu'à deux (creux 1 et creux 2) ; les emplacements
 * au-delà restent disponibles sans rien coûter.
 */
export function unitDecorationSlots(nodeCount, startSign) {
    const slots = [];
    let index = 0;
    let sign = startSign;
    for (const span of unitBends(nodeCount)) {
        // Un virage d'un seul pas ne dessine aucun creux : rien à décorer.
        if (span >= 2) {
            slots.push({
                // Le décor s'ancre à la frontière AVANT ce nœud.
                afterNode: index + Math.floor(span / 2),
                // Virage de longueur impaire : le sommet tombe ENTRE deux
                // nœuds, le décor descend d'un demi-pas pour rester au milieu
                // du creux plutôt que collé au nœud du dessus.
                shift: span % 2 === 0 ? 0 : NODE_PITCH / 2,
                // Le creux est du côté opposé au virage.
                side: sign > 0 ? "left" : "right",
            });
        }
        index += span;
        sign = -sign;
    }
    return slots;
}

/**
 * Sens de départ de l'unité SUIVANTE : l'opposé du dernier virage de
 * celle-ci. C'est ce qui fait du parcours une seule ligne continue au lieu
 * d'un damier — deux renflements du même côté ne se collent jamais de part et
 * d'autre d'une bannière.
 */
export function nextUnitSign(nodeCount, startSign) {
    const count = unitBends(nodeCount).length;
    if (count === 0) return startSign;
    const lastSign = count % 2 === 1 ? startSign : -startSign;
    return -lastSign;
}
